import { useRouter } from "vue-router"
import { inject, ref, nextTick } from "vue"
import { defineStore, storeToRefs } from "pinia"

import { useSessionStorage } from "@/helpers/useSessionStorage"
import { useUserStore } from "@/stores/user"

import type { Translate } from "@/plugins/i18n/types"
import { type GlobalMachineEvent } from "@/machines/types"

import { patchAssessment } from "@/api/viktor/assessment/assessment"
import { useCookies } from "@vueuse/integrations/useCookies"
import { COOKIES } from "@/config/constants"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { globalActor } from "@/machines/globalMachine"

export const useHeaderControlsStore = defineStore("headerControls", () => {
  const t = inject("t") as Translate
  const isModalOpen = ref(false)
  const isModalSubmitDisabled = ref(false)

  const router = useRouter()
  const userStore = useUserStore()
  const { setSessionItem } = useSessionStorage()
  const { fetchNextAssessment } = userStore
  const { nextAssessmentName } = storeToRefs(userStore)

  const handleNotPassed = async (assessmentSlug: string) => {
    const trackingName = `${assessmentSlug}_not-passed`

    const isAssessmentNotPassed =
      userStore.nextAssessment?.next_assessment?.extra_data?.is_not_passed

    if (isAssessmentNotPassed) {
      MixpanelInstance.track(trackingName)
    } else {
      MixpanelInstance.track(trackingName, { is_first_time: true })
    }

    await updateNotPassedAssessment(assessmentSlug)
  }

  const updateNotPassedAssessment = async (assessmentSlug: string) => {
    try {
      if (isModalSubmitDisabled.value) {
        return
      }
      isModalSubmitDisabled.value = true

      await patchAssessment(assessmentSlug, {
        not_passed: true,
      })
      await closeNotPassedAndRedirect(assessmentSlug)
    } catch {
      return
    } finally {
      isModalSubmitDisabled.value = false
    }
  }

  const afAuthSession = useCookies().get("af-session-auth")
  const sharepool = afAuthSession?.sharepool

  const closeNotPassedAndRedirect = async (assessmentSlug: string) => {
    // Show toast only for this session, do not persist if the user close page
    const toastText = sharepool
      ? t("assessment.accessibility.skip_assessment.toaster.success", {
          assessment: t(`general.tests.name.${assessmentSlug}`),
        })
      : t("assessment.accessibility.skip_assessment.toaster.success_alt", {
          assessment: t(`general.tests.name.${assessmentSlug}`),
        })
    setSessionItem("toastText", toastText)
    isModalOpen.value = false

    const pm_uuid = useCookies().get(COOKIES.PM_UUID)
    const company_uuid = useCookies().get(COOKIES.COMPANY_UUID)
    await fetchNextAssessment(pm_uuid ?? null, company_uuid ?? null)

    await nextTick()

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.PM_UUID)
      useCookies().remove(COOKIES.COMPANY_UUID)
      await router.push({ name: "stepper" })
    }
  }

  return {
    handleNotPassed,
    isModalOpen,
    isModalSubmitDisabled,
  }
})
